import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProductionShiftService } from 'src/app/services/production-shift/production-shift.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { AlertService, MessageSeverity } from 'src/app/shared/services/alert.service';
import { MessageModalComponent } from 'src/app/shared/components/modals/message/message-modal.component';
import { ProductionShiftFormModalComponent } from './production-shift-form-modal/production-shift-form-modal.component';

@Component({
  selector: 'app-production-shift-crud',
  templateUrl: './production-shift-crud.component.html',
  styleUrls: ['./production-shift-crud.component.css']
})
export class ProductionShiftCrudComponent implements OnInit, OnChanges {
  @Input() idActuator: number = 0;
  @Input() idShift: number = 0;
  @Input() actuators: any[] = [];
  @Input() filterTrigger: number = 0;
  @Output() goBack = new EventEmitter<void>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  
  loading: boolean = false;
  dataSource = new MatTableDataSource();
  displayedColumns: string[] = ['id', 'actuador', 'turno', 'fechaInicio', 'fechaFin', 'estado', 'distance_projected', 'distance_quality', 'distance_produced', 'weft_density_ppcm', 'acciones'];
  
  pageEvent: PageEvent;
  pageIndex: number = 0;
  pageSize: number = AppConstants.Paginado.DEFAULT_PAGE_SIZE_PAGINATION;
  length: number = 0;
  page: number = 1;

  // Eliminamos las propiedades del formulario inline ya que ahora usamos modal

  constructor(
    private productionShiftService: ProductionShiftService,
    private spinner: NgxSpinnerService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar cambios en los filtros y recargar datos
    // Solo recargar si cambian los parámetros reales de filtrado, no solo el trigger
    if (changes['idActuator'] || changes['idShift']) {
      this.loadData();
    }
    // Si solo cambia el filterTrigger, no recargar automáticamente
    // para evitar dobles llamadas al cambiar de tab
  }

  loadData() {
    this.spinner.show();
    this.loading = true;

    const parameters = {
      page: this.page,
      pageSize: this.pageSize,
      idActuator: this.idActuator,
      idShift: this.idShift
    };

    this.productionShiftService.getList(parameters).subscribe(
      (response) => {
        if (response.error == null) {
          this.dataSource.data = response.rows;
          this.length = response.total;
          this.pageIndex = response.page - 1;
        } else {
          this.alertService.showMessage(response.message, '', MessageSeverity.error);
        }
        this.loading = false;
        this.spinner.hide();
      },
      (error) => {
        this.alertService.showMessage('Error al cargar los registros', '', MessageSeverity.error);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  onPageChange(event: PageEvent) {
    this.pageEvent = event;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.page = event.pageIndex + 1;
    this.loadData();
  }

  showAddRecordForm() {
    const dialogRef = this.dialog.open(ProductionShiftFormModalComponent, {
      width: '95vw',
      maxWidth: '900px',
      maxHeight: '95vh',
      data: {
        record: null, // Nuevo registro
        actuators: this.actuators,
        idActuator: this.idActuator
      },
      disableClose: true,
      panelClass: 'responsive-modal'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.loadData(); // Recargar la tabla
      }
    });
  }

  editRecord(record: any) {
    const dialogRef = this.dialog.open(ProductionShiftFormModalComponent, {
      width: '95vw',
      maxWidth: '900px',
      maxHeight: '95vh',
      data: {
        record: record, // Registro a editar
        actuators: this.actuators,
        idActuator: this.idActuator
      },
      disableClose: true,
      panelClass: 'responsive-modal'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.loadData(); // Recargar la tabla
      }
    });
  }

  // Método saveRecord eliminado - ahora se maneja en el modal

  deleteRecord(record: any) {
    let dialogRef = this.dialog.open(MessageModalComponent, {
      data: {
        title: AppConstants.TitleModal.DELETE_CONFIRMATION_TITLE,
        message: `¿Estás seguro que deseas eliminar este registro de producción? Luego de completada la acción no se podrá deshacer.`,
        hasButton: true,
        contentButton: {
          yes: 'Sí, eliminar',
          no: 'Cancelar'
        }
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response.result == true) {
        this.spinner.show();
        this.productionShiftService.delete(record.id).subscribe(
          (response) => {
            if (response.code == 200) {
              this.alertService.showMessage('Registro eliminado correctamente', '', MessageSeverity.success);
              this.loadData();
            } else {
              this.alertService.showMessage(response.message, '', MessageSeverity.error);
            }
            this.spinner.hide();
          },
          (error) => {
            this.alertService.showMessage('Error al eliminar el registro', '', MessageSeverity.error);
            this.spinner.hide();
          }
        );
      }
    });
  }

  // Métodos cancelForm, resetForm y validateForm eliminados - ahora se manejan en el modal

  formatDateTimeForInput(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = this.padZero(date.getMonth() + 1);
    const day = this.padZero(date.getDate());
    const hours = this.padZero(date.getHours());
    const minutes = this.padZero(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formatDateTimeForAPI(dateTime: string): string {
    if (!dateTime) return '';
    // Convertir de "2025-09-05T06:50" a "2025-09-05 06:50:00"
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = this.padZero(date.getMonth() + 1);
    const day = this.padZero(date.getDate());
    const hours = this.padZero(date.getHours());
    const minutes = this.padZero(date.getMinutes());
    const seconds = this.padZero(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  private padZero(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  getDuration(fechaInicio: string, fechaFin: string): string {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffMs = fin.getTime() - inicio.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  getRecordStatus(record: any): string {
    const now = new Date();
    const inicio = new Date(record.fechaInicio);
    const fin = new Date(record.fechaFin);
    
    if (now < inicio) {
      return 'status-pending';
    } else if (now >= inicio && now <= fin) {
      return 'status-active';
    } else {
      return 'status-completed';
    }
  }

  getRecordStatusText(record: any): string {
    const now = new Date();
    const inicio = new Date(record.fechaInicio);
    const fin = new Date(record.fechaFin);
    
    if (now < inicio) {
      return 'Pendiente';
    } else if (now >= inicio && now <= fin) {
      return 'En Progreso';
    } else {
      return 'Completado';
    }
  }

  goBackToParent() {
    this.goBack.emit();
  }

  getShiftName(shiftId?: number): string {
    const shift = shiftId !== undefined ? shiftId : 0;
    switch (shift) {
      case 0:
        return 'Todos';
      case 1:
        return 'Día';
      case 2:
        return 'Noche';
      default:
        return 'Desconocido';
    }
  }

  getActuatorName(actuatorId?: number): string {
    const id = actuatorId !== undefined ? actuatorId : 0;
    const actuator = this.actuators.find(a => a.id === id);
    return actuator ? actuator.name || actuator.sensor || actuator.nomenclature : 'Desconocido';
  }

  isRecordCompleted(record: any): boolean {
    // Un registro está completado si tiene fecha fin
    return record && record.endDate && record.endDate.trim() !== '';
  }

  canEditRecord(record: any): boolean {
    // Solo se puede editar si no está completado
    return !this.isRecordCompleted(record);
  }

  canDeleteRecord(record: any): boolean {
    // Solo se puede eliminar si no está completado
    return !this.isRecordCompleted(record);
  }

  // Métodos de paradas programadas eliminados - ahora se manejan en el modal

}